import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../utils/googleMapsLoader";

interface LatLng {
  lat: number;
  lng: number;
}

interface Props {
  origin: LatLng;
  destination: LatLng;
  height?: string;
}

export default function LiveDirectionsMap({ origin, destination, height = "420px" }: Props) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const youMarkerRef = useRef<google.maps.Marker | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const watchIdRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [liveError, setLiveError] = useState("");
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setRouteInfo(null);

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapDivRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
          center: origin,
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
        });
        mapRef.current = map;

        const directionsService = new google.maps.DirectionsService();
        const directionsRenderer = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#ea580c", strokeWeight: 5 },
        });

        // Hedef: standart kırmızı konum iğnesi. Ok ikonu "sen buradasın" gibi algılanıyordu.
        new google.maps.Marker({
          position: destination,
          map,
          title: "Teslimat adresi",
        });

        directionsService.route(
          { origin, destination, travelMode: google.maps.TravelMode.DRIVING },
          (result, status) => {
            if (cancelled) return;
            if (status === "OK" && result) {
              directionsRenderer.setDirections(result);
              const leg = result.routes[0]?.legs[0];
              if (leg) {
                setRouteInfo({ distance: leg.distance?.text ?? "", duration: leg.duration?.text ?? "" });
              }
            } else {
              setError("Rota hesaplanamadı.");
            }
            setLoading(false);
          }
        );

        const youMarker = new google.maps.Marker({
          position: origin,
          map,
          title: "Konumunuz",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: "#2563eb",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          },
          zIndex: 999,
        });
        youMarkerRef.current = youMarker;

        if (navigator.geolocation) {
          watchIdRef.current = navigator.geolocation.watchPosition(
            (pos) => {
              const pt = { lat: pos.coords.latitude, lng: pos.coords.longitude };
              youMarkerRef.current?.setPosition(pt);
              mapRef.current?.panTo(pt);
              setLiveError("");
            },
            () => setLiveError("Canlı konumunuz alınamıyor — rota son bilinen konumla gösteriliyor."),
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Harita yüklenemedi.");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin.lat, origin.lng, destination.lat, destination.lng]);

  return (
    <div style={{ position: "relative", height }}>
      {routeInfo && (
        <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 10,
          background: "#111827", color: "#fff", borderRadius: "8px",
          padding: "6px 12px", fontSize: "0.82rem", fontWeight: 600,
          display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
        }}>
          <i className="bi bi-signpost-split-fill"></i>
          {routeInfo.distance} · {routeInfo.duration}
        </div>
      )}
      <div style={{
        position: "absolute", top: 10, right: 10, zIndex: 10,
        background: "#fff", borderRadius: "8px", padding: "6px 10px",
        fontSize: "0.75rem", fontWeight: 600, color: "#374151",
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", gap: 4,
      }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#2563eb", border: "2px solid #fff", boxShadow: "0 0 0 1px #2563eb", marginRight: 6 }} />Siz</span>
        <span><i className="bi bi-geo-alt-fill" style={{ color: "#dc2626", marginRight: 4 }}></i>Teslimat adresi</span>
      </div>
      <div ref={mapDivRef} style={{ width: "100%", height: "100%", background: "#f1f5f9" }} />
      {loading && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.75)" }}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}
      {error && (
        <div style={{
          position: "absolute", bottom: 10, left: 10, right: 10, zIndex: 10,
          background: "#fef2f2", border: "1.5px solid #fecaca", color: "#b91c1c",
          borderRadius: "8px", padding: "8px 12px", fontSize: "0.8rem",
        }}>
          <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
      )}
      {!error && liveError && (
        <div style={{
          position: "absolute", bottom: 10, left: 10, right: 10, zIndex: 10,
          background: "#fffbeb", border: "1.5px solid #fde68a", color: "#92400e",
          borderRadius: "8px", padding: "8px 12px", fontSize: "0.8rem",
        }}>
          <i className="bi bi-exclamation-triangle me-1"></i>{liveError}
        </div>
      )}
    </div>
  );
}
